import React, { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { MdFileDownload } from 'react-icons/md';

import axios from 'axios';
import { Colors, Spacing } from '../theme/Theme';
import { LoadingIndicator } from './LoadingIndicator';


const PDFViewer = ({...props}) => {

    const [pdfDataUrl, setPdfDataUrl] = useState('');
    const [numPages, setNumPages] = useState<any>(null);
    const [hasError, setHasError] = useState(false);
    // Track the current object URL so it can be revoked (avoids a memory leak
    // from URL.createObjectURL never being released).
    const objectUrlRef = useRef<string>('');

    useEffect(() => {
        let cancelled = false;

        async function fetchAndStorePDF() {
          setHasError(false);
          // Release any previously created object URL before creating a new one.
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = '';
          }
          setPdfDataUrl('');
          try {
            const response = await axios.get(props.pdfUrl, { responseType: 'arraybuffer' });
            if (cancelled) return;
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const pdfData = URL.createObjectURL(pdfBlob);
            objectUrlRef.current = pdfData;
            setPdfDataUrl(pdfData);
          } catch (error) {
            console.error('Error fetching PDF:', error);
            if (!cancelled) setHasError(true);
          }
        }

        if (props.pdfUrl) {
          fetchAndStorePDF();
        }

        return () => {
          cancelled = true;
        };
      }, [props.pdfUrl]);

    // Revoke the object URL when the component unmounts.
    useEffect(() => {
      return () => {
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = '';
        }
      };
    }, []);

    // Reuse the already-fetched Blob to trigger a browser download without a
    // second network request. Only surfaced for the Shift Contract viewer.
    const isDownloadable = props?.title === "Shift Contract";
    const downloadingRef = useRef(false);
    const downloadPdf = () => {
      if (!pdfDataUrl || downloadingRef.current) return;
      downloadingRef.current = true;
      try {
        const link = document.createElement('a');
        link.href = pdfDataUrl;
        link.download = props?.fileName || 'Shift-Agreement.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading PDF:', error);
      } finally {
        // Guard against accidental rapid double-clicks producing multiple files.
        setTimeout(() => {
          downloadingRef.current = false;
        }, 800);
      }
    };

    const isImageViewer = !(props?.title == "resume" || props?.title == "Shift Contract");


  return (
    <div>
      {isDownloadable && (
        // pointerEvents:auto re-enables clicks even though the parent viewer
        // wrapper sets pointerEvents:none to keep the rendered PDF view-only.
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            pointerEvents: 'auto',
            marginBottom: Spacing.small,
          }}
        >
          <button
            type="button"
            onClick={downloadPdf}
            disabled={!pdfDataUrl}
            title="Download agreement"
            aria-label="Download agreement"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: pdfDataUrl ? 'pointer' : 'not-allowed',
              border: 'none',
              background: 'transparent',
              color: pdfDataUrl ? Colors.themeBlue : Colors.defaultGrey,
              fontSize: 15,
              fontWeight: 600,
              padding: `${Spacing.smallest}px ${Spacing.small}px`,
            }}
          >
            <MdFileDownload size={22} />
            Download
          </button>
        </div>
      )}
      {hasError ? (
        <div
          style={{
            pointerEvents: 'auto',
            textAlign: 'center',
            padding: Spacing.large,
            color: Colors.red,
            fontSize: 15,
          }}
        >
          Unable to load the document. Please close and reopen it, or check your
          internet connection.
        </div>
      ) : !pdfDataUrl ? (
        <LoadingIndicator />
      ) : isImageViewer ? (
        <img
          src={pdfDataUrl}
          alt="logo"
          style={{
            display: "flex",
            flex: 1,
            maxWidth: "90%",
            maxHeight: "100%",
            marginBottom: Spacing.large,
          }}
        />
      ) : (
        <Document
          file={pdfDataUrl}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={() => setHasError(true)}
        >
          {Array.apply(null, Array(numPages))
            .map((x, i) => i + 1)
            .map(page => <Page pageNumber={page} />)}
        </Document>
      )}
    </div>
  );
};

export default PDFViewer;
